import type { Entity } from '@backstage/catalog-model';
import {
  CAPABILITIES,
  CAPABILITY_AREAS,
} from '../../modules/capabilities/capabilities';
import type { DataSphereCapability } from '../../modules/capabilities/capabilities';

/**
 * Template annotation that puts a template under a heading on the Services
 * page. The value is the `id` of one of the groups below.
 */
export const SERVICE_GROUP_ANNOTATION = 'datasphere.howden.com/service-group';

export type ServiceGroup = {
  id: string;
  title: string;
  description: string;
};

export type GroupedServices = {
  group: ServiceGroup;
  services: Entity[];
  /** Roadmap services in this group that are not built yet. */
  planned: DataSphereCapability[];
};

/**
 * The headings on the Services page, in display order. They are the
 * capability areas, less "Cost, Estate and Quality", which holds pages rather
 * than request forms.
 */
export const SERVICE_GROUPS: ServiceGroup[] = CAPABILITY_AREAS.filter(
  area => area.id !== 'insights',
);

/** Catches templates with no group, or a group id that is not listed above. */
export const OTHER_SERVICES_GROUP: ServiceGroup = {
  id: 'other',
  title: 'Other Services',
  description: 'Services that are not yet in a group.',
};

const serviceTitle = (entity: Entity) =>
  entity.metadata.title ?? entity.metadata.name;

/**
 * Sorts templates into the Services page groups. Groups keep the order of
 * SERVICE_GROUPS, the "Other" group comes last, empty groups are left out, and
 * services in a group are sorted by title. With `includePlanned`, each group
 * also lists its roadmap services that are not live yet, and a group with only
 * planned services is kept.
 */
export function groupServices(
  templates: Entity[],
  { includePlanned = false }: { includePlanned?: boolean } = {},
): GroupedServices[] {
  const known = new Set(SERVICE_GROUPS.map(group => group.id));
  const groupIdOf = (entity: Entity) => {
    const id = entity.metadata.annotations?.[SERVICE_GROUP_ANNOTATION];
    return id && known.has(id) ? id : OTHER_SERVICES_GROUP.id;
  };

  return [...SERVICE_GROUPS, OTHER_SERVICES_GROUP]
    .map(group => ({
      group,
      services: templates
        .filter(entity => groupIdOf(entity) === group.id)
        .sort((a, b) => serviceTitle(a).localeCompare(serviceTitle(b))),
      planned: includePlanned
        ? CAPABILITIES.filter(
            capability =>
              capability.area === group.id &&
              capability.availability !== 'live',
          )
        : [],
    }))
    .filter(
      ({ services, planned }) => services.length > 0 || planned.length > 0,
    );
}
